/* PlatformGridViewRenderer.js */

"use strict";
/* global kendo, $ */
const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const Registrations = require("../../../../../RegistrationNamespaces.js");
const DistinctValuesFeed = require("../../../../lib/DistinctValuesFeed.js");
const TreeUtils = require("../../../siteexplorer/treeUtilities.js");
const _ = require("lodash");

class PlatformGridViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("odm-os-platform-grid-view-gl");
        this._logger = this._model.logger;
        this._treeContainer = document.createElement("tree-container");
        this._gridContainer = document.createElement("grid-container-wrapper");

        this._documentWidth = document.body.clientWidth;
        this._treeContainer.style.width = 200 + "px";
        this._panelEl = document.createElement("span");
        this._panelEl.classList.add("active", "treeview-toggle");
        this._panelEl.setAttribute("title", "Sites");
        this._panelEl.innerHTML = `<actionable-icon-gl data-icon="topology.solution" data-dark-theme="true" class="dark"></actionable-icon-gl>`;

        this._updateFilter = this._updateFilter.bind(this);
        this._userDataStorage = this._model._storageFactory.getRemoteStorageInstance(
            "view",
            "odm_os_platform_details"
        );
        this._defaultViewActive = false;
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on(
            "FeedInitialized",
            (this._onFeedInitialized = this._onFeedInitialized.bind(this))
        );

        // this._model.gameEditor.on('ColumnSettingsChange', this._onColumnSettingsChanged = this._onColumnSettingsChanged.bind(this));
        this._model.on(
            "GridColumnUpdated",
            (this._onGridColumnUpdated = this._onGridColumnUpdated.bind(this))
        );
        this._model.pagingFeed.on(
            "RefreshView",
            (this._onFeedRefreshView = this._onFeedRefreshView.bind(this))
        );

        this._model._gameEditor._LMRBLayout.on(
            "SelectRowsOnDeviceGrid",
            (this._onSelectRowsOnDeviceGrid = this._onSelectRowsOnDeviceGrid.bind(this))
        );
        this._model._gameEditor.on(
            "OrganizationChanged",
            (this._onOrgChanged = this._onOrgChanged.bind(this))
        );

        window.addEventListener(
            "resize",
            (this._onWindowResize = () => {
                this._documentWidth = document.body.clientWidth;
                this._treeContainer.style.width =
                    (this._userDataStorage.getData("treeContainerWidth", true) || 200) + "px";
            })
        );
        this._model._gameEditor.on(
            "RegenerateTreeData",
            (this._onRegenerateTreeData = this._onRegenerateTreeData.bind(this))
        );
        this._registerGridListeners();
    }

    _registerGridListeners() {
        this._platformEntityGrid.on(
            "UserSelectedRows",
            (this._onGridUserSelectedRows = this._onGridUserSelectedRows.bind(this))
        );
        this._platformEntityGrid.on(
            "UserRowDoubleClicked",
            (this._onUserDblClickedRow = this._onUserDblClickedRow.bind(this))
        );
        this._platformEntityGrid.on(
            "UserSorted",
            (this._onUserSorted = this._onUserSorted.bind(this))
        );
        this._platformEntityGrid.on(
            "GridBusyStart",
            (this._onGridBusyStart = this._onGridBusyStart.bind(this))
        );
        // this._platformEntityGrid.on("BasicSearchFilterChangeData", (this._onFilterChange = this._onFilterChange.bind(this)));
        // this._platformEntityGrid.on("AdvancedSearchFilterChangeData", (this._onFilterChange = this._onFilterChange.bind(this)));
    }

    _unregisterListeners() {
        if (this._model) {
            this._model.removeListener("FeedInitialized", this._onFeedInitialized);
            this._model.removeListener("GridColumnUpdated", this._onGridColumnUpdated);
        }
        window.removeEventListener("resize", this._onWindowResize);

        if (this._model.pagingFeed) {
            this._model.pagingFeed.removeListener("RefreshView", this._onFeedRefreshView);
        }
        if (this._model._gameEditor._LMRBLayout) {
            this._model._gameEditor._LMRBLayout.removeListener(
                "SelectRowsOnDeviceGrid",
                this._onSelectRowsOnDeviceGrid
            );
        }
        if (this._model._gameEditor) {
            this._model._gameEditor.removeListener(
                "RegenerateTreeData",
                this._onRegenerateTreeData
            );
            this._model._gameEditor.removeListener("OrganizationChanged", this._onOrgChanged);
        }
        if (this._platformEntityGrid) {
            this._unregisterGridListeners();
        }
    }

    _unregisterGridListeners() {
        this._platformEntityGrid.removeAllListeners("UserSelectedRows");
        this._platformEntityGrid.removeAllListeners("UserRowDoubleClicked");
        this._platformEntityGrid.removeListener("UserSorted", this._onUserSorted);
        this._platformEntityGrid.removeListener("GridBusyStart", this._onGridBusyStart);
        // this._platformEntityGrid.removeListener("BasicSearchFilterChangeData", this._onFilterChange);
        // this._platformEntityGrid.removeListener("AdvancedSearchFilterChangeData", this._onFilterChange);
    }

    async _renderAvailableActions() {
        let numSelected = this.selectedRows.length || 0;

        // Create and Find Images actions are always enabled
        this._model.gameEditor.enableAction(Registrations.PLATFORM_EXPLORER_ACTION_CREATE);
        this._model.gameEditor.enableAction(Registrations.PLATFORM_EXPLORER_ACTION_FIND_IMAGES);

        // Delete, Add Images, and Add Instructions actions require selection
        if (numSelected > 0) {
            this._model.gameEditor.enableAction(Registrations.PLATFORM_EXPLORER_ACTION_DELETE);
            this._model.gameEditor.enableAction(Registrations.PLATFORM_EXPLORER_ACTION_ADD_IMAGES);
            this._model.gameEditor.enableAction(
                Registrations.PLATFORM_EXPLORER_ACTION_ADD_INSTRUCTIONS
            );
        } else {
            this._model.gameEditor.disableAction(Registrations.PLATFORM_EXPLORER_ACTION_DELETE);
            this._model.gameEditor.disableAction(Registrations.PLATFORM_EXPLORER_ACTION_ADD_IMAGES);
            this._model.gameEditor.disableAction(
                Registrations.PLATFORM_EXPLORER_ACTION_ADD_INSTRUCTIONS
            );
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        if (this._moObserver) {
            this._moObserver.disconnect();
        }
        let promArr = [
            _.isUndefined(this._platformEntityGrid)
                ? Promise.resolve()
                : this._platformEntityGrid.shutdown(),
        ];

        return Promise.all(promArr).then(
            () => {
                this._platformEntityGrid = undefined;
                this._defaultViewActive = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this.model._logger.warn(err, "PlatformGridViewRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        // const treeFilterData = this._userDataStorage.getData("DE_site_filter_data");
        const selectedSite = this._userDataStorage.getData("site_tree_selected");
        return this._model
            .whenModelIsReady(selectedSite)
            .then(() => {
                this.rootElement.appendChild(this._gridContainer);

                this._platformEntityGrid = new EntityGrid({
                    gridKey: this.model._gridKey,
                    userGridData: this._model._userGridData,
                    logger: this._logger,
                    singleSelect: false,
                    columns: this._model.columnConfig,
                    pagingFeed: this._model.pagingFeed,
                    selectedRows: _.get(
                        this._model.getSelectedOdmOsPlatformIds(),
                        "selectedRows",
                        []
                    ),
                    showHeader: true,
                    showLinesNumber: this._model._gameEditor.renderer._rootElem.querySelector(
                        "game-editor-view-actions-gl left-container"
                    ),
                    columnSettingsWidgetContainer:
                        this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                    groupingContainer:
                        this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                    filterComponent:
                        this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                    advancedFilterComponent:
                        this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                    modalUserFeedback: this._model._modalUserFeedback,
                    groupingValuesFeed: new DistinctValuesFeed({
                        startFeed: (fieldName, query, replacementValue) => {
                            if (replacementValue) {
                                return this.model._OdmOsPlatformEntityDef.startOdmOsPlatformDistinctValuesFeed(
                                    this.model.activeOrgId,
                                    fieldName,
                                    query,
                                    replacementValue
                                );
                            }
                            return this.model._OdmOsPlatformEntityDef.startOdmOsPlatformDistinctValuesFeed(
                                this.model.activeOrgId,
                                fieldName,
                                query
                            );
                        },
                        replacementValue: "Blank",
                    }),
                    externalFilter: this._model._filterForTree,
                });

                if (!this._gridContainer.hasChildNodes()) {
                    this._gridContainer.appendChild(this._platformEntityGrid.renderer.rootElement);
                }

                this._renderAvailableActions();
                this._onFeedRefreshView();
                this._registerListeners();
            })
            .then(() => {
                // const openedTabs = this.model.explorerState.openedTabs;
                // let openedTabsIds = Object.keys(openedTabs);

                // if (this._model._gameEditor._filterFromUrl) {
                //     this._platformEntityGrid._advancedFilter._advancedFilter._renderer._applyQuery();
                // }

                // if (
                //     this._model._gameEditor._openEditorFromUrl &&
                //     this._model._gameEditor._openEditorFromUrl.open &&
                //     !_.isUndefined(this._model._gameEditor._openEditorFromUrl.selectedIds)
                // ) {
                //     openedTabsIds = this._model._gameEditor._openEditorFromUrl.selectedIds;
                // }

                // const activeTabId = this.model.explorerState.activeTab;
                // let activeTab = openedTabs[activeTabId];

                // if (
                //     openedTabs &&
                //     openedTabsIds.length > 0 &&
                //     this.model.gameEditor._editorLayout.launchedEditors.length === 0
                // ) {
                //     delete openedTabsIds[activeTabId];

                //     openedTabsIds.forEach((tabId) => {
                //         if (!this._model._gameEditor._openEditorFromUrl.open) {
                //             const openedTab = openedTabs[tabId],
                //                 ids = openedTab.deviceIds;
                //             if (typeof openedTab === "object" && tabId !== activeTabId) {
                //                 this.model.openPlatformInstanceEditor(ids, openedTab.fqn);
                //             }
                //         } else {
                //             this.model.openPlatformInstanceEditor(tabId);
                //             activeTab = undefined;
                //         }
                //     });

                //     // Open the active tab at the end
                //     if (typeof activeTab === "object") {
                //         this.model.openPlatformInstanceEditor(activeTab.deviceIds, activeTab.fqn);
                //     }
                // }
                return;
            })
            .catch((error) => {
                this._logger.warn(
                    {error},
                    "PlatformGridViewRenderer.render  --  model is not ready -- error"
                );
            });
    }

    get rootElement() {
        return this._rootElem;
    }

    get model() {
        return this._model;
    }

    get selectedRows() {
        return this._platformEntityGrid.getSelectedRows();
    }

    restartGrid() {
        this._unregisterGridListeners();
        return this._platformEntityGrid.shutdown().then(() => {
            this._instantiateGrid();
            this._gridContainer.appendChild(this._platformEntityGrid.renderer.rootElement);
            this._registerGridListeners();
            this._renderAvailableActions();
        });
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _showDefaultMessageDisplay() {
        let message = {
            message: "Create a new platform",
            icon: "osmanager.add_platform",
            action: Registrations.PLATFORM_EXPLORER_ACTION_CREATE,
        };

        return this._model.gameEditor
            .showDefaultMessageView(message, this._gridContainer)
            .then(() => {
                this._defaultViewActive = true;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "DeviceListViewerRenderer._showDefaultMessageDisplay error"
                );
            });
    }

    _hideDefaultMessageDisplay() {
        return this._model.gameEditor
            .hideDefaultMessageView()
            .then(() => {
                this._defaultViewActive = false;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "DeviceListViewerRenderer._hideDefaultMessageDisplay error"
                );
            });
    }

    // _onFilterChange() {
    // if (this._model._selectedNode && !_.isEmpty(this._model._filterForTree)) {
    //     let pagingFilter = this._model.pagingFeed.getFilterData();
    //     if (pagingFilter.$userQuery) {
    //         pagingFilter = pagingFilter.$userQuery;
    //     }

    //     const filter = Array.isArray(pagingFilter) ?
    //         { "$and": pagingFilter.concat([this._model._filterForTree]) } :
    //         {
    //             "$and": [
    //                 pagingFilter,
    //                 this._model._filterForTree
    //             ]
    //         };
    //     this._model.pagingFeed.setFilter(filter);
    // }
    // }

    _updateFilter(node, panelHidden = false, silent = false) {
        if (node === "ontoggle") {
            if (panelHidden) {
                this._platformEntityGrid.clearExternalFilter(silent);
            } else {
                if (!_.isEmpty(this._model._filterForTree)) {
                    this._platformEntityGrid.setExternalFilter(this._model._filterForTree, silent);
                } else {
                    this._platformEntityGrid.clearExternalFilter(silent);
                }
            }
        } else {
            if (!node || !node._pristineProperties || !node._pristineProperties.path) {
                this._platformEntityGrid.clearExternalFilter(silent);
                this._model._filterForTree = {};
                this._userDataStorage.setData("", "site_tree_selected");
            } else {
                const pathVals = TreeUtils.getAllChildrenPaths(node);

                this._model._filterForTree = {sitePath: {$in: pathVals}};
                this._userDataStorage.setData(node._id, "site_tree_selected");
                this._userDataStorage.setData(
                    JSON.stringify(this._model._filterForTree),
                    "site_tree_filter_data"
                );
                this._platformEntityGrid.setExternalFilter(this._model._filterForTree);
            }
        }
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onGridBusyStart() {
        if (this._model.pagingFeed.totalEntities > this._model.pagingFeed.pageSize * 2) {
            let gridBusyEnded = false;
            this._platformEntityGrid.once("GridBusyEnd", () => {
                gridBusyEnded = true;
            });
            this._model._modalUserFeedback.displayLoading().then(() => {
                if (gridBusyEnded) {
                    this._model._modalUserFeedback.hideDisplayed();
                } else {
                    this._platformEntityGrid.once("GridBusyEnd", () => {
                        this._model._modalUserFeedback.hideDisplayed();
                    });
                }
            });
        }
    }

    _onFeedInitialized() {
        // this._renderGridRows();
        this._renderAvailableActions();
    }

    _onUserSorted(eventData) {
        this._model.onUserSorted(eventData);
    }

    _onUserDblClickedRow(obj) {
        if (!obj.selectedRow) {
            return;
        }
        const view =
            this._userDataStorage.getData("PE_active_view", true) ||
            Registrations.ODM_OS_PLATFORM_EDITOR_VIEW_DETAILS;

        this._model.openPlatformInstanceEditor(obj.selectedRow, view);
    }

    _onGridUserSelectedRows(selectedRowIds) {
        this._model._gameEditor._editorLayout.emit("UserSelectedRows", selectedRowIds || []);
        this._model.updateExplorerState("selectedIds", selectedRowIds || []);
        this._renderAvailableActions();
    }

    _onFeedRefreshView() {
        const totalEntities = this._model.pagingFeed.totalEntities;
        const filterData = this._model.pagingFeed.getFilterData();
        if (totalEntities === 0 && _.isEmpty(_.get(filterData, "$userQuery", filterData))) {
            if (this._defaultViewActive === false) {
                this._showDefaultMessageDisplay();
            }
        } else {
            if (this._defaultViewActive === true) {
                this._hideDefaultMessageDisplay();
            }
        }
    }
    _onGridColumnUpdated() {
        this._model.gameEditor.onGridColumnUpdated();
    }

    _onSelectRowsOnDeviceGrid(rowIdsToSelect) {
        this._platformEntityGrid.setSelectedRows(rowIdsToSelect);
    }

    _onOrgChanged() {
        this._userDataStorage.setData("", "site_tree_selected");
        this._model._filterForTree = {};
        this._userDataStorage.setData(
            JSON.stringify(this._model._filterForTree),
            "site_tree_filter_data"
        );
    }

    _onResizerMousedown(e) {
        let treeWidth = this._treeContainer.style.width;
        e = e || window.event;
        let start = 0,
            diff = 0;
        if (e.pageX) {
            start = e.pageX;
        } else if (e.clientX) {
            start = e.clientX;
        }

        document.body.onmousemove = (e) => {
            e = e || window.event;
            let end = 0;
            if (e.pageX) {
                end = e.pageX;
            } else if (e.clientX) {
                end = e.clientX;
            }

            diff = end - start;
            let updatedTreeWidth = this._treeContainer.style.width;
            if (parseInt(updatedTreeWidth, 10) > 100 || diff > 0) {
                this._treeContainer.style.width = parseInt(treeWidth, 10) + diff + "px";
            }
        };
        document.body.onmouseup = () => {
            this._userDataStorage.setData(
                parseInt(this._treeContainer.style.width, 10),
                "treeContainerWidth"
            );
            document.body.onmousemove = document.body.onmouseup = null;
        };
    }

    _onRegenerateTreeData() {
        let tv = $("tree-container").data("kendoTreeView");
        this._model.treeData = TreeUtils.generateTreeNodesFromRaw(
            this._model.gameEditor.sitePaths,
            "path"
        ).items;

        let treeDat = this._model.treeData;
        tv.setDataSource(treeDat);
        const selectedPath = _.get(
            this._model,
            "_selectedNode._pristineProperties.path",
            _.get(this._model, "_selectedNode._pristineProperties.name")
        );
        if (selectedPath) {
            setTimeout(() => {
                const elWithPath = this._rootElem.querySelector(
                    'span[data-path="' + selectedPath + '"'
                );
                if (elWithPath) {
                    tv = $("tree-container").data("kendoTreeView");
                    const nodeEl = $(elWithPath).closest(".k-item");
                    const upNode = tv.dataItem(nodeEl);
                    tv.select(nodeEl);
                    this._model._selectedNode = upNode;
                    this._updateFilter(upNode);
                    return;
                }
                this._updateFilter();
                return;
            }, 500);
        } else {
            this._updateFilter();
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    // TODO - Nobody should be using methods on a Renderer directly so make all these methods private

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = PlatformGridViewRenderer;
